import { useState, useEffect, useRef } from "react";
import swal from 'sweetalert2';
import ReactApexChart from "react-apexcharts";
import RightSideDetails from "../global/rightSideDetails";
import { getMembershipDashboard, getSubscriptionValidityForAdmin } from "../api/paymentAPI";
import UpdateMembershipModal from "./updateMembershipModal";
import subscription_expired from '../../assets/images/subscription_expired.png';

const ShimmerBox = ({ height, style = {} }) => (
    <div className="klgr-shimmer" style={{ height, borderRadius: 4, ...style }} />
);

const ShimmerPortlet = ({ bodyHeight }) => (
    <div className="kt-portlet kt-portlet--bordered" style={{ marginBottom: 0 }}>
        <div className="kt-portlet__head">
            <ShimmerBox height={18} style={{ width: '40%', margin: '14px 0' }} />
        </div>
        <div className="kt-portlet__body">
            <ShimmerBox height={bodyHeight} />
        </div>
    </div>
);

const DashboardSkeleton = () => (
    <>
        <style>{`
            @keyframes klgr-shimmer {
                0%   { background-position: -200% 0; }
                100% { background-position:  200% 0; }
            }
            .klgr-shimmer {
                background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: klgr-shimmer 1.4s ease-in-out infinite;
                border-radius: 4px;
            }
        `}</style>

        {/* Row 1 — KPI cards */}
        <div className="row margin-b20">
            {[0,1,2,3,4,5].map(i => (
                <div className="col-md-2" key={i}>
                    <ShimmerPortlet bodyHeight={60} />
                </div>
            ))}
        </div>

        {/* Row 1b — secondary KPI cards */}
        <div className="row margin-b20">
            {[0,1,2].map(i => (
                <div className="col-md-4" key={i}>
                    <ShimmerPortlet bodyHeight={60} />
                </div>
            ))}
        </div>

        {/* Row 2 — Type Donut + Bar Chart */}
        <div className="row margin-b20">
            <div className="col-md-5"><ShimmerPortlet bodyHeight={280} /></div>
            <div className="col-md-7"><ShimmerPortlet bodyHeight={280} /></div>
        </div>

        {/* Row 3 — Term Donut + Birthdays */}
        <div className="row margin-b20">
            <div className="col-md-5"><ShimmerPortlet bodyHeight={300} /></div>
            <div className="col-md-7"><ShimmerPortlet bodyHeight={300} /></div>
        </div>

        {/* Row 4 — Expiring Soon + Expired */}
        <div className="row margin-b20">
            <div className="col-md-6"><ShimmerPortlet bodyHeight={160} /></div>
            <div className="col-md-6"><ShimmerPortlet bodyHeight={160} /></div>
        </div>

        {/* Row 5 — Recent Subs */}
        <div className="row margin-b20">
            <div className="col-md-12"><ShimmerPortlet bodyHeight={200} /></div>
        </div>
    </>
);

const MembershipDashboard = (props) => {

    const [datePreset,           setDatePreset]           = useState('current_month');
    const [fromDate,             setFromDate]             = useState('');
    const [toDate,               setToDate]               = useState('');
    const [dashboardData,        setDashboardData]        = useState(null);
    const [loading,              setLoading]              = useState(false);
    const [isExpiringSoonInit,   setIsExpiringSoonInit]   = useState(true);
    const [isRecentSubsInit,     setIsRecentSubsInit]     = useState(true);
    const [isExpiredMembersInit, setIsExpiredMembersInit] = useState(true);
    const [birthdayData,         setBirthdayData]         = useState(null);
    const [anniversaryData,      setAnniversaryData]      = useState(null);
    const [birthdayView,         setBirthdayView]         = useState('birthdays');
    const celebrationInitRef                              = useRef(false);
    const [rightSideDetails,     setRightSideDetails]     = useState(false);
    const [selectedMember,       setSelectedMember]       = useState(null);
    const [membershipValidity,   setMembershipValidity]   = useState([]);
    const [membershipRecord,     setMembershipRecord]     = useState({});
    const [showMembershipModal,  setShowMembershipModal]  = useState(false);
    const [subRefresh,           setSubRefresh]           = useState(false);
    const [subNavState,          setSubNavState]          = useState('Current Membership');
    const [showCustomPicker,     setShowCustomPicker]     = useState(false);

    const subStatusMap = { 'Current Membership': 'Active', 'Upcoming': 'Upcoming', 'Expired': 'Expired' };

    const resolveDateRange = (preset) => {
        const today = moment();
        switch (preset) {
            case 'current_month': return { fromDate: today.clone().startOf('month').format('YYYY-MM-DD'), toDate: today.format('YYYY-MM-DD') };
            case 'next_month':    return { fromDate: today.clone().add(1, 'month').startOf('month').format('YYYY-MM-DD'), toDate: today.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD') };
            case '3_months':      return { fromDate: today.format('YYYY-MM-DD'), toDate: today.clone().add(3, 'months').format('YYYY-MM-DD') };
            default:              return null;
        }
    };

    useEffect(() => {
        props.title('Membership Dashboard', 'membership-dashboard');
        const range = resolveDateRange('current_month');
        setFromDate(range.fromDate);
        setToDate(range.toDate);
        fetchDashboard(range.fromDate, range.toDate);
    }, []);

    useEffect(() => {
        if (!showCustomPicker) return;
        const t = setTimeout(() => {
            $('#dpMDFromDate').datepicker({ format: 'dd-mm-yyyy' });
            $('#dpMDFromDate').off('change').on('change', function (e) {
                setFromDate(moment(e.target.value, 'DD-MM-YYYY').format('YYYY-MM-DD'));
            });
            $('#dpMDToDate').datepicker({ format: 'dd-mm-yyyy' });
            $('#dpMDToDate').off('change').on('change', function (e) {
                setToDate(moment(e.target.value, 'DD-MM-YYYY').format('YYYY-MM-DD'));
            });
        }, 0);
        return () => {
            clearTimeout(t);
            try { $('#dpMDFromDate').datepicker('destroy'); } catch (e) {}
            try { $('#dpMDToDate').datepicker('destroy'); } catch (e) {}
        };
    }, [showCustomPicker]);

    useEffect(() => {
        if (rightSideDetails) {
            Config.Core.OpenDrawer();
        } else {
            setMembershipValidity([]);
            setMembershipRecord({});
            setSubNavState('Current Membership');
        }
    }, [rightSideDetails]);

    useEffect(() => {
        if (rightSideDetails && selectedMember) {
            fetchSubscriptionValidity();
        }
    }, [rightSideDetails, subRefresh]);

    useEffect(() => {
        if (dashboardData) {
            if (dashboardData.expiringSoon.length > 0)       fillExpiringSoonTable();
            if (dashboardData.recentSubs.length > 0)         fillRecentSubsTable();
            if (dashboardData.expiredMembers?.length > 0)    fillExpiredMembersTable();
        }
    }, [dashboardData]);

    useEffect(() => {
        if (!dashboardData) return;
        const data = birthdayView === 'birthdays' ? birthdayData : anniversaryData;
        if (!data?.length) return;
        fillCelebrationTable(data, birthdayView === 'birthdays');
    }, [birthdayView, birthdayData, anniversaryData, dashboardData]);

    const fetchDashboard = async (fDate, tDate) => {
        try {
            setLoading(true);
            setDashboardData(null);
            setIsExpiringSoonInit(true);
            setIsRecentSubsInit(true);
            setIsExpiredMembersInit(true);
            celebrationInitRef.current = false;
            setBirthdayView('birthdays');
            const res = await getMembershipDashboard(fDate, tDate);
            setBirthdayData(res?.birthDays);
            setAnniversaryData(res?.anniversaries);
            setDashboardData(res);
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 300);
        }
    };

    const fetchSubscriptionValidity = async () => {
        try {
            const res = await getSubscriptionValidityForAdmin(selectedMember.GroupID);
            setMembershipValidity(res);
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message });
        }
    };

    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        if (preset === 'custom') {
            setShowCustomPicker(true);
            return;
        }
        setShowCustomPicker(false);
        const range = resolveDateRange(preset);
        setFromDate(range.fromDate);
        setToDate(range.toDate);
        fetchDashboard(range.fromDate, range.toDate);
    };

    const handleApply = () => {
        if (!fromDate || !toDate) {
            swal.fire({ icon: 'warning', titleText: 'Please select both From Date and To Date', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }
        if (fromDate > toDate) {
            swal.fire({ icon: 'warning', titleText: 'From Date cannot be after To Date', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }
        setShowCustomPicker(false);
        fetchDashboard(fromDate, toDate);
    };

    const closeDrawer = () => {
        Config.Core.CloseDrawer();
        setTimeout(() => {
            setRightSideDetails(false);
            setSelectedMember(null);
        }, 200);
    };

    function calculateProgress(startDate, endDate) {
        const start = new Date(startDate);
        const end   = new Date(endDate);
        const now   = new Date();
        return `${Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))}%`;
    }

    function fillExpiringSoonTable() {
        if (!isExpiringSoonInit) $('#tblExpiringSoon').KTDatatable().destroy();
        
        const dataTable = $('#tblExpiringSoon').KTDatatable({
            data: { saveState: false, source: dashboardData.expiringSoon, pageSize: 20 },
            layout: { scroll: false, footer: false },
            sortable: true,
            pagination: false,
            columns: [
                {
                    field: 'Name',
                    title: 'Member',
                    width: 200,
                    autoHide: false,
                    template: (row) => `<div class="kt-user-card-v2">
                        <div class="kt-user-card-v2__details text-clip">
                            <span class="kt-user-card-v2__name text-clip">${row.Name}</span>
                            <span class="kt-user-card-v2__desc text-clip">${row.MembershipNo || ''}</span>
                        </div>
                    </div>`,
                },
                { field: 'SubscriptionName', title: 'Membership', width: 200, autoHide: false },
                {
                    field: 'SubscriptionExpiresOn',
                    title: 'Expires On',
                    width: 130,
                    autoHide: false,
                    template: (row) => row.SubscriptionExpiresOn ? moment(row.SubscriptionExpiresOn).format('DD-MMM-YYYY') : '-',
                },
                {
                    field: 'DaysLeft',
                    title: 'Days Left',
                    width: 130,
                    autoHide: false,
                    template: (row) => {
                        const d = row.DaysLeft;
                        if (d <= 7)  return `<span class="kt-badge kt-badge--danger kt-badge--pill" style="padding: 8px 33px; color: #fff; white-space: nowrap;">${d} Day${d === 1 ? '' : 's'} Left</span>`;
                        if (d <= 15) return `<span class="kt-badge kt-badge--grey kt-badge--pill" style="padding: 8px 33px; color: #fd397a; white-space: nowrap;">${d} Days Left</span>`;
                        return `<span class="kt-badge kt-badge--success kt-badge--pill" style="padding: 8px 44px; color: #fff; white-space: nowrap;">${d} Days Left</span>`;
                    },
                },
            ],
        });
        setIsExpiringSoonInit(false);
        $('#root .kt-grid--hor').addClass('max-width100');
        $('#tblExpiringSoon .kt-datatable__table').addClass('overflow-x-auto');
        Config.Core.GrabAndScroll('.kt-datatable__table');

        dataTable.on('click', 'tr', function () {
            const index = $('tr').index(this) - 1;
            if (dataTable.dataSet[index]) {
                setSelectedMember(dataTable.dataSet[index]);
                setRightSideDetails(true);
            }
        });
    }

    function fillExpiredMembersTable() {
        if (!isExpiredMembersInit) $('#tblExpiredMembers').KTDatatable().destroy();

        const dataTable = $('#tblExpiredMembers').KTDatatable({
            data: { saveState: false, source: dashboardData.expiredMembers, pageSize: 20 },
            layout: { scroll: false, footer: false },
            sortable: true,
            pagination: false,
            columns: [
                {
                    field: 'Name',
                    title: 'Member',
                    width: 200,
                    autoHide: false,
                    template: (row) => `<div class="kt-user-card-v2">
                        <div class="kt-user-card-v2__details text-clip">
                            <span class="kt-user-card-v2__name text-clip">${row.Name}</span>
                            <span class="kt-user-card-v2__desc text-clip">${row.MembershipNo || ''}</span>
                        </div>
                    </div>`,
                },
                { field: 'SubscriptionName', title: 'Membership', width: 200, autoHide: false },
                {
                    field: 'SubscriptionExpiresOn',
                    title: 'Expired On',
                    width: 130,
                    autoHide: false,
                    template: (row) => row.SubscriptionExpiresOn ? moment(row.SubscriptionExpiresOn).format('DD-MMM-YYYY') : '-',
                },
            ],
        });
        setIsExpiredMembersInit(false);
        $('#tblExpiredMembers .kt-datatable__table').addClass('overflow-x-auto');
        Config.Core.GrabAndScroll('.kt-datatable__table');

        dataTable.on('click', 'tr', function () {
            const index = $('tr').index(this) - 1;
            if (dataTable.dataSet[index]) {
                setSelectedMember(dataTable.dataSet[index]);
                setRightSideDetails(true);
            }
        });
    }

    function fillRecentSubsTable() {
        if (!isRecentSubsInit) $('#tblRecentSubs').KTDatatable().destroy();
        $('#tblRecentSubs').KTDatatable({
            data: { saveState: false, source: dashboardData.recentSubs, pageSize: 10 },
            layout: { scroll: false, footer: false },
            sortable: false,
            pagination: false,
            columns: [
                { field: 'MemberName',    title: 'Member',       width: 180, autoHide: false },
                { field: 'MembershipNo',  title: 'Membership No', width: 140, autoHide: false },
                { field: 'SubscriptionName', title: 'Membership', width: 180, autoHide: false },
                {
                    field: 'SubscriptionStartsFrom',
                    title: 'Starts From',
                    width: 130,
                    autoHide: false,
                    template: (row) => row.SubscriptionStartsFrom ? moment(row.SubscriptionStartsFrom).format('DD-MMM-YYYY') : '-',
                },
                {
                    field: 'SubscriptionExpiresOn',
                    title: 'Expires On',
                    width: 130,
                    autoHide: false,
                    template: (row) => row.SubscriptionExpiresOn ? moment(row.SubscriptionExpiresOn).format('DD-MMM-YYYY') : '-',
                },
                {
                    field: 'Total',
                    title: 'Total (₹)',
                    width: 110,
                    autoHide: false,
                    template: (row) => `₹${row.Total || 0}`,
                },
            ],
        });
        setIsRecentSubsInit(false);
        $('#tblRecentSubs .kt-datatable__table').addClass('overflow-x-auto');
    }

    function buildWhatsappUrl(mobile, message) {
        const digits = mobile ? mobile.replace(/\D/g, '') : '';
        if (!digits) return null;
        const phone = digits.length === 10 ? '91' + digits
                    : digits.length === 12 && digits.startsWith('91') ? digits
                    : digits;
        return `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
    }

    function fillCelebrationTable(data, isBirthday) {
        if (celebrationInitRef.current) {
            try { $('#tblCelebration').KTDatatable().destroy(); } catch (e) {}
        }
        const birthdayColumns = [
            { field: 'Name', title: 'Member', width: 180, autoHide: false },
            {
                field: 'DOB', title: 'Birthday', width: 120, autoHide: false,
                template: (row) => row.DOB ? moment(row.DOB).format('DD MMMM') : '-',
            },
            {
                field: 'Mobile', title: '', width: 40, autoHide: false,
                template: (row) => {
                    const msg = `Dear ${row.Name}, wishing you a very Happy Birthday! 🎂🎉 May this special day bring you joy and happiness. - Team KLGR`;
                    const url = buildWhatsappUrl(row.Mobile, msg);
                    if (!url) return '-';
                    return `<a href="${url}" target="_blank" title="Send Birthday wish on WhatsApp" style="color:#25D366;font-size:18px;"><i class="socicon socicon-whatsapp"></i></a>`;
                },
            },
        ];
        const anniversaryColumns = [
            { field: 'Name', title: 'Member', width: 180, autoHide: false },
            {
                field: 'Anniversary', title: 'Anniversary', width: 120, autoHide: false,
                template: (row) => row.Anniversary ? moment(row.Anniversary).format('DD MMMM') : '-',
            },
            {
                field: 'Mobile', title: '', width: 40, autoHide: false,
                template: (row) => {
                    const msg = `Dear ${row.Name}, wishing you a very Happy Anniversary! 🎊💐 May your bond grow stronger with each passing year. - Team KLGR`;
                    const url = buildWhatsappUrl(row.Mobile, msg);
                    if (!url) return '-';
                    return `<a href="${url}" target="_blank" title="Send Anniversary wish on WhatsApp" style="color:#25D366;font-size:18px;"><i class="socicon socicon-whatsapp"></i></a>`;
                },
            },
        ];
        $('#tblCelebration').KTDatatable({
            data: { saveState: false, source: data, pageSize: 10 },
            layout: { scroll: false, footer: false },
            sortable: false,
            pagination: false,
            columns: isBirthday ? birthdayColumns : anniversaryColumns,
        });
        celebrationInitRef.current = true;
        $('#tblCelebration .kt-datatable__table').addClass('overflow-x-auto');
    }

    const summary = dashboardData?.summary || {};

    const donutSeries  = (dashboardData?.membershipBreakdown || []).map(r => Number(r.MemberCount));
    const donutOptions = {
        chart: { type: 'donut' },
        labels: (dashboardData?.membershipBreakdown || []).map(r => r.MembershipTypeName || 'Unknown'),
        colors: ['#0abb87', '#5867dd', '#fd397a', '#ffb822', '#2590bb'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        responsive: [{ breakpoint: 480, options: { legend: { position: 'bottom' } } }],
    };

    const termSeries  = (dashboardData?.membershipTermBreakdown || []).map(r => Number(r.MemberCount));
    const termOptions = {
        chart: { type: 'donut' },
        labels: (dashboardData?.membershipTermBreakdown || []).map(r => r.MembershipTerm || 'Unknown'),
        colors: ['#0abb87', '#ffb822', '#5867dd', '#fd397a', '#00bcd4', '#f4516c'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
        responsive: [{ breakpoint: 480, options: { legend: { position: 'bottom' } } }],
    };

    const barSeries  = [{ name: 'Members', data: (dashboardData?.subscriptionStatusBands || []).map(r => Number(r.Count)) }];
    const barOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 4 } },
        xaxis: { categories: (dashboardData?.subscriptionStatusBands || []).map(r => r.Band) },
        colors: ['#5867dd'],
        dataLabels: { enabled: false },
        yaxis: { title: { text: 'Members' } },
    };

    const periodLabel = datePreset === 'custom'
        ? `${fromDate ? moment(fromDate).format('DD MMM YYYY') : '—'} – ${toDate ? moment(toDate).format('DD MMM YYYY') : '—'}`
        : ({ current_month: 'Current Month', next_month: 'Next Month', '3_months': '3 Months' }[datePreset] || '');

    const kpiCards = [
        {
            label: 'Total Active Members', value: summary.TotalActiveMembers || 0,
            hex: '#4c48d9', iconBg: 'rgba(76,72,217,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 21a8 8 0 0 0-16 0"/><circle cx="10" cy="8" r="5"/><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/></svg>,
        },
        {
            label: 'Golf Members', value: summary.GolfMembers || 0,
            hex: '#0abb87', iconBg: 'rgba(10,187,135,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 8 6-3-6-3v10"/><path d="m8 11.99-5.5 3.14a1 1 0 0 0 0 1.74l8.5 4.86a2 2 0 0 0 2 0l8.5-4.86a1 1 0 0 0 0-1.74L16 12"/><path d="m6.49 12.85 11.02 6.3"/><path d="M17.51 12.85 6.5 19.15"/></svg>,
        },
        {
            label: 'Social Members', value: summary.SocialMembers || 0,
            hex: '#5578eb', iconBg: 'rgba(85,120,235,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-1a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v1"/><path d="M19 10h1a2 2 0 0 1 2 2v1"/><path d="M5 10H4a2 2 0 0 0-2 2v1"/><circle cx="12" cy="11" r="3"/><circle cx="18" cy="4" r="2"/><circle cx="6" cy="4" r="2"/></svg>,
        },
        {
            label: 'Lifetime Members', value: summary.LifetimeMembers || 0,
            hex: '#7c4dff', iconBg: 'rgba(124,77,255,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
        },
        {
            label: 'Complimentary Golf', value: summary.ComplimentaryGolfMembers || 0,
            hex: '#fd397a', iconBg: 'rgba(253,57,122,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 8 6-3-6-3v10"/><path d="m8 11.99-5.5 3.14a1 1 0 0 0 0 1.74l8.5 4.86a2 2 0 0 0 2 0l8.5-4.86a1 1 0 0 0 0-1.74L16 12"/><path d="m6.49 12.85 11.02 6.3"/><path d="M17.51 12.85 6.5 19.15"/></svg>,
        },
        {
            label: 'Complimentary Social', value: summary.ComplimentarySocialMembers || 0,
            hex: '#00bcd4', iconBg: 'rgba(0,188,212,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-1a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v1"/><path d="M19 10h1a2 2 0 0 1 2 2v1"/><path d="M5 10H4a2 2 0 0 0-2 2v1"/><circle cx="12" cy="11" r="3"/><circle cx="18" cy="4" r="2"/><circle cx="6" cy="4" r="2"/></svg>,
        },
    ];

    const kpiCardsSecondary = [
        {
            label: 'Active Memberships', value: summary.ActiveSubscriptions || 0,
            hex: '#5be38d', iconBg: 'rgba(91,227,141,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.5 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4"/><path d="M2 10h20"/><path d="M6 14h2"/><path d="m16 17 2 2 4-4"/></svg>,
        },
        {
            label: 'Expired Memberships', value: summary.ExpiredSubscriptions || 0,
            hex: '#fd397a', iconBg: 'rgba(253,57,122,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.5 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3.5"/><path d="M2 10h20"/><path d="M6 14h2"/><path d="m16.5 14.5 5 5"/><path d="m21.5 14.5-5 5"/></svg>,
        },
        {
            label: 'Expiring Soon (≤15 days)', value: summary.ExpiringSoon || 0,
            hex: '#ffb822', iconBg: 'rgba(255,184,34,0.12)',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a9.75 9.75 0 0 1 6.74 2.74"/><path d="M18.74 5.74 21 8"/><path d="M21 8V3"/><path d="M7.5 19.794c-6-3.464-6-12.124 0-15.588"/><path d="M7.5 4.206A9 9 0 0 1 12 3"/><path d="M12 7v5l4 2"/><path d="M14 20.775A9 9 0 0 1 12 21"/><path d="M19 17.656a9 9 0 0 1-1.5 1.456"/><path d="M21 12a9 9 0 0 1-.228 2"/><path d="M21 8h-5"/></svg>,
        },
    ];

    return (
        <>
            <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor padding-tb0" id="kt_content">
                <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid margin-t20">

                    {/* Date Range Selector */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', marginBottom: 20 }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <div style={{ display: 'flex', background: '#f0f0f5', borderRadius: 8, padding: 3, gap: 2 }}>
                                {[
                                    { key: 'current_month', label: 'Current Month' },
                                    { key: 'next_month',    label: 'Next Month'    },
                                    { key: '3_months',      label: '3 Months'      },
                                    { key: 'custom',        label: 'Custom'        },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => handlePresetChange(key)}
                                        style={{
                                            border: 'none', borderRadius: 6, padding: '8px 20px',
                                            fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                                            background: datePreset === key ? '#5867dd' : 'transparent',
                                            color:      datePreset === key ? '#fff'    : '#74788d',
                                            transition: 'background 0.15s, color 0.15s',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                            {datePreset === 'custom' && (
                                <>
                                    <button
                                        onClick={() => setShowCustomPicker(p => !p)}
                                        style={{
                                            marginLeft: 8,
                                            border: '1.5px solid #ffb31d', borderRadius: 6,
                                            background: '#fff', color: '#48465b',
                                            padding: '4px 12px', fontSize: '0.8rem', fontWeight: 500,
                                            cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex',
                                            alignItems: 'center', gap: 6,
                                        }}>
                                        <i className="la la-calendar" style={{ color: '#ffb31d', fontSize: '1rem' }}></i>
                                        {fromDate ? moment(fromDate).format('DD MMM YYYY') : '—'} – {toDate ? moment(toDate).format('DD MMM YYYY') : '—'}
                                    </button>
                                    {showCustomPicker && (
                                        <div style={{
                                            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 999,
                                            background: '#fff', border: '1px solid #e2e5ec', borderRadius: 6,
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '16px', minWidth: 260,
                                        }}>
                                            <div style={{ marginBottom: 10 }}>
                                                <label style={{ fontSize: '0.78rem', color: '#74788d', display: 'block', marginBottom: 3 }}>From</label>
                                                <div className="input-group date">
                                                    <input
                                                        id="dpMDFromDate"
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Select date"
                                                        readOnly={true}
                                                        defaultValue={fromDate ? moment(fromDate).format('DD-MM-YYYY') : ''}
                                                    />
                                                    <div className="input-group-append">
                                                        <span className="input-group-text"><i className="la la-calendar-check-o"></i></span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: 12 }}>
                                                <label style={{ fontSize: '0.78rem', color: '#74788d', display: 'block', marginBottom: 3 }}>To</label>
                                                <div className="input-group date">
                                                    <input
                                                        id="dpMDToDate"
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Select date"
                                                        readOnly={true}
                                                        defaultValue={toDate ? moment(toDate).format('DD-MM-YYYY') : ''}
                                                    />
                                                    <div className="input-group-append">
                                                        <span className="input-group-text"><i className="la la-calendar-check-o"></i></span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button
                                                    className="btn btn-brand btn-sm"
                                                    style={{ flex: 1 }}
                                                    onClick={handleApply}>
                                                    Apply
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    style={{ flex: 1 }}
                                                    onClick={() => setShowCustomPicker(false)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Loading */}
                    {loading && <DashboardSkeleton />}

                    {/* Dashboard Content */}
                    {dashboardData && (
                        <>
                            {/* KPI Cards — row 1: access-level counts */}
                            <div className="row">
                                {kpiCards.map((card, i) => (
                                    <div className="col-md-2" key={i}>
                                        <div className="kt-portlet kt-portlet--bordered" style={{ marginBottom: 16 }}>
                                            <div className="kt-portlet__body" style={{ padding: '16px 18px' }}>
                                                {/* Icon + Value row */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                                                        background: card.iconBg, color: card.hex,
                                                    }}>
                                                        {card.icon}
                                                    </div>
                                                    <span style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1, color: card.hex }}>
                                                        {card.value}
                                                    </span>
                                                </div>
                                                {/* Label */}
                                                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#48465b', lineHeight: 1.3 }}>
                                                    {card.label}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* KPI Cards — row 2: fee-tier counts */}
                            <div className="row" style={{ marginBottom: 16 }}>
                                {kpiCardsSecondary.map((card, i) => (
                                    <div className="col-md-4" key={i}>
                                        <div className="kt-portlet kt-portlet--bordered" style={{ marginBottom: 0 }}>
                                            <div className="kt-portlet__body" style={{ padding: '16px 18px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                                                        background: card.iconBg, color: card.hex,
                                                    }}>
                                                        {card.icon}
                                                    </div>
                                                    <span style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1, color: card.hex }}>
                                                        {card.value}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#48465b', lineHeight: 1.3 }}>
                                                    {card.label}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Charts */}
                            <div className="row" style={{ alignItems: 'stretch' }}>
                                <div className="col-md-5" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--bordered" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <div className="kt-portlet__head">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Membership Type Breakdown</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body" style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                                            {donutSeries.length > 0 ? (
                                                <div style={{ width: '100%' }}>
                                                    <ReactApexChart options={donutOptions} series={donutSeries} type="donut" height={380} />
                                                </div>
                                            ) : (
                                                <div className="text-center padding-t20 padding-b20" style={{ width: '100%' }}>
                                                    <p className="kt-font-muted">No data for selected period</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-7">
                                    <div className="kt-portlet kt-portlet--bordered">
                                        <div className="kt-portlet__head">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Membership Status Breakdown</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body">
                                            {barSeries[0].data.length > 0 ? (
                                                <ReactApexChart options={barOptions} series={barSeries} type="bar" height={300} />
                                            ) : (
                                                <div className="text-center padding-t20 padding-b20">
                                                    <p className="kt-font-muted">No data for selected period</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Membership Term Donut + Birthdays Row */}
                            <div className="row" style={{ alignItems: 'stretch' }}>
                                <div className="col-md-5" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--bordered" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <div className="kt-portlet__head">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Membership Term Breakdown</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body" style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                                            {termSeries.length > 0 ? (
                                                <div style={{ width: '100%' }}>
                                                    <ReactApexChart options={termOptions} series={termSeries} type="donut" height={380} />
                                                </div>
                                            ) : (
                                                <div className="text-center padding-t20 padding-b20" style={{ width: '100%' }}>
                                                    <p className="kt-font-muted">No data for selected period</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-7" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--mobile" style={{ flex: 1 }}>
                                        <div className="kt-portlet__head kt-portlet__head--lg">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">
                                                    {birthdayView === 'birthdays' ? 'Upcoming Birthdays' : 'Upcoming Anniversaries'}
                                                </span>
                                            </div>
                                            <div className="kt-portlet__head-toolbar">
                                                <div style={{ display: 'flex', background: '#f0f0f5', borderRadius: 6, padding: 2, gap: 1 }}>
                                                    {[
                                                        { key: 'birthdays',     label: 'Birthdays'     },
                                                        { key: 'anniversaries', label: 'Anniversaries' },
                                                    ].map(({ key, label }) => (
                                                        <button
                                                            key={key}
                                                            onClick={() => setBirthdayView(key)}
                                                            style={{
                                                                border: 'none', borderRadius: 5, padding: '4px 10px',
                                                                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                                                                background: birthdayView === key ? '#5867dd' : 'transparent',
                                                                color:      birthdayView === key ? '#fff'    : '#74788d',
                                                                transition: 'background 0.15s, color 0.15s',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body kt-portlet__body--fit">
                                            {birthdayView === 'birthdays' && (birthdayData?.length || 0) === 0 && (
                                                <div className="text-center" style={{ paddingTop: 100, paddingBottom: 30 }}>
                                                    <p className="kt-font-muted">No birthdays in the next 30 days</p>
                                                </div>
                                            )}
                                            {birthdayView === 'anniversaries' && (anniversaryData?.length || 0) === 0 && (
                                                <div className="text-center" style={{ paddingTop: 100, paddingBottom: 30 }}>
                                                    <p className="kt-font-muted">No anniversaries in the next 30 days</p>
                                                </div>
                                            )}
                                            {((birthdayView === 'birthdays' && (birthdayData?.length || 0) > 0) ||
                                              (birthdayView === 'anniversaries' && (anniversaryData?.length || 0) > 0)) && (
                                                <div id="tblCelebration" className="kt-datatable table-striped"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Expiring Soon + Expired Row */}
                            <div className="row" style={{ alignItems: 'stretch' }}>
                                <div className="col-md-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--mobile" style={{ flex: 1 }}>
                                        <div className="kt-portlet__head kt-portlet__head--lg">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Expiring Soon</span>
                                            </div>
                                            <div className="kt-portlet__head-toolbar">
                                                <span style={{ fontSize: '0.78rem', color: '#5867dd', background: 'rgba(88,103,221,0.1)', borderRadius: 4, padding: '3px 10px', fontWeight: 600 }}>{periodLabel}</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body kt-portlet__body--fit">
                                            {dashboardData.expiringSoon.length === 0 ? (
                                                <div className="text-center padding-t50 padding-b30">
                                                    <p className="kt-font-muted">No members expiring soon in this period</p>
                                                </div>
                                            ) : (
                                                <div id="tblExpiringSoon" className="kt-datatable table-striped clickable"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--mobile" style={{ flex: 1 }}>
                                        <div className="kt-portlet__head kt-portlet__head--lg">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Expired Memberships</span>
                                            </div>
                                            <div className="kt-portlet__head-toolbar">
                                                <span style={{ fontSize: '0.78rem', color: '#5867dd', background: 'rgba(88,103,221,0.1)', borderRadius: 4, padding: '3px 10px', fontWeight: 600 }}>{periodLabel}</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body kt-portlet__body--fit">
                                            {(dashboardData.expiredMembers?.length || 0) === 0 ? (
                                                <div className="text-center padding-t50 padding-b30">
                                                    <p className="kt-font-muted">No expired memberships in this period</p>
                                                </div>
                                            ) : (
                                                <div id="tblExpiredMembers" className="kt-datatable table-striped clickable"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Recently Added Memberships Row */}
                            <div className="row" style={{ alignItems: 'stretch' }}>
                                <div className="col-md-12" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="kt-portlet kt-portlet--mobile" style={{ flex: 1 }}>
                                        <div className="kt-portlet__head kt-portlet__head--lg">
                                            <div className="kt-portlet__head-label">
                                                <span className="kt-portlet__head-title">Recently Added Memberships</span>
                                            </div>
                                            <div className="kt-portlet__head-toolbar">
                                                <span style={{ fontSize: '0.78rem', color: '#5867dd', background: 'rgba(88,103,221,0.1)', borderRadius: 4, padding: '3px 10px', fontWeight: 600 }}>{periodLabel}</span>
                                            </div>
                                        </div>
                                        <div className="kt-portlet__body kt-portlet__body--fit">
                                            {dashboardData.recentSubs.length === 0 ? (
                                                <div className="text-center padding-t30 padding-b30">
                                                    <p className="kt-font-muted">No memberships added in this period</p>
                                                </div>
                                            ) : (
                                                <div id="tblRecentSubs" className="kt-datatable table-striped"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Drawer */}
                    {rightSideDetails && selectedMember && (
                        <RightSideDetails title="Membership Details" onCloseDrawer={closeDrawer}>

                            {/* Member header */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="kt-portlet">
                                        <div className="kt-portlet__body">
                                            <div className="kt-widget kt-widget--user-profile-3">
                                                <div className="kt-widget__top">
                                                    <div className="kt-widget__pic kt-widget__pic--brand kt-font-brand kt-font-boldest kt-hidden-">
                                                        {selectedMember.Name?.slice(0, 2)}
                                                    </div>
                                                    <div className="kt-widget__content">
                                                        <div className="kt-widget__head">
                                                            <span className="kt-widget__username">
                                                                {selectedMember.Name}
                                                                &nbsp;&nbsp;&nbsp;
                                                                <span className={`kt-badge kt-badge--${selectedMember.MembershipType == 1 ? 'success' : 'info'} kt-badge--inline kt-badge--pill padding-lr10`}>
                                                                    {selectedMember.MembershipType == 1 ? 'Golf' : 'Social'}
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className="kt-widget__subhead padding-t0">
                                                            <span className="padding-r10">
                                                                <i className="flaticon2-user"></i>
                                                                {selectedMember.MembershipNo}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Subscription section */}
                            <div className="kt-portlet kt-portlet--mobile">
                                <div className="kt-portlet__head kt-portlet__head--lg padding-l0">
                                    <div className="kt-portlet__head-label">
                                        <nav className="file-nav navbar navbar-top">
                                            {['Current Membership', 'Upcoming', 'Expired'].map(tab => (
                                                <a
                                                    key={tab}
                                                    className={subNavState === tab ? 'top-nav-active' : ''}
                                                    onClick={() => setSubNavState(tab)}
                                                >
                                                    {tab}
                                                </a>
                                            ))}
                                        </nav>
                                    </div>
                                    {(subNavState === 'Upcoming' || (subNavState === 'Current Membership' && membershipValidity.some(s => {
                                        if (s.Status !== 'Active') return false;
                                        const today = new Date(); today.setHours(0, 0, 0, 0);
                                        const expiry = new Date(s.SubscriptionExpiresOn + 'T00:00:00');
                                        return Math.round((expiry - today) / 86400000) + 1 <= 7;
                                    }))) && (
                                        <div className="kt-portlet__head-toolbar">
                                            <div className="kt-portlet__head-wrapper">
                                                <div className="kt-portlet__head-actions">
                                                    <button
                                                        className="btn btn-label-brand btn-block margin-0"
                                                        onClick={(e) => { e.preventDefault(); setMembershipRecord({}); setShowMembershipModal(true); }}
                                                    >
                                                        <i className="la la-plus"></i>
                                                        Add New Membership
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="kt-portlet__body padding-t0">
                                    <div className="padding-lr10 padding-tb20">
                                        {membershipValidity.filter(s => s.Status === subStatusMap[subNavState]).length === 0 && (
                                            <div style={{ textAlign: 'center' }} className="padding-t20">
                                                <img src={subscription_expired} alt="membership-expired" height="255" width="350" style={{ opacity: '0.6' }} />
                                                <h5>No {subNavState === 'Current Membership' ? 'Active' : subNavState} Membership</h5>
                                            </div>
                                        )}
                                        {membershipValidity
                                            .filter(s => s.Status === subStatusMap[subNavState])
                                            .sort((a, b) => subNavState === 'Upcoming'
                                                ? (new Date(a.SubscriptionStartsFrom) - new Date(b.SubscriptionStartsFrom))
                                                  || (new Date(a.SubscriptionExpiresOn) - new Date(b.SubscriptionExpiresOn))
                                                : 0
                                            )
                                            .map((subscription, index) => {
                                                const badgeClass = subscription.Status === 'Active'  ? 'success'
                                                                 : subscription.Status === 'Expired' ? 'grey'
                                                                 : 'brand';
                                                const fontClass  = subscription.Status === 'Active'  ? 'success'
                                                                 : subscription.Status === 'Expired' ? 'danger'
                                                                 : 'brand';
                                                return (
                                                    <div
                                                        key={index}
                                                        className="kt-portlet"
                                                        onClick={() => { setMembershipRecord(subscription); setShowMembershipModal(true); }}
                                                        style={{ boxShadow: '0px 0px 13px 0px rgb(82 63 105 / 25%)' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0px 0px 20px 0px rgb(82 63 105 / 40%)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0px 0px 13px 0px rgb(82 63 105 / 25%)'}
                                                    >
                                                        <div className="kt-portlet__body">
                                                            <div className="kt-section kt-section--space-md" style={{ marginBottom: '2px' }}>
                                                                <div className="kt-widget24 kt-widget24--solid">
                                                                    <div className="kt-widget24__details">
                                                                        <div className="kt-widget24__info">
                                                                            <a href="#" className="kt-widget24__title" title="Click to edit">
                                                                                {subscription.SubscriptionName}
                                                                                &nbsp;&nbsp;&nbsp;
                                                                                <span className={`kt-badge kt-badge--${badgeClass} kt-badge--inline kt-badge--pill padding-lr10`}>
                                                                                    {subscription.Status}
                                                                                </span>
                                                                            </a>
                                                                            <span className="kt-widget24__desc">{subscription.Code}</span><br />
                                                                            <span className="kt-widget24__desc margin-tb5 padding-r100">{subscription.Description}</span>
                                                                        </div>
                                                                        <span className={`kt-widget24__stats kt-font-${fontClass} padding-l100`}>
                                                                            ₹{subscription.Total}
                                                                        </span>
                                                                    </div>
                                                                    <div className="progress progress--sm">
                                                                        <div
                                                                            className={`progress-bar kt-bg-${badgeClass}`}
                                                                            role="progressbar"
                                                                            style={{ width: calculateProgress(subscription.SubscriptionStartsFrom, subscription.SubscriptionExpiresOn) }}
                                                                            aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                    <div className="kt-widget24__action">
                                                                        <span className="kt-widget24__change">
                                                                            {moment(subscription.SubscriptionStartsFrom).format('DD-MMM-YYYY')}
                                                                        </span>
                                                                        <span className="kt-widget24__number">
                                                                            {moment(subscription.SubscriptionExpiresOn).format('DD-MMM-YYYY')}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            </div>

                        </RightSideDetails>
                    )}

                </div>
            </div>

            {showMembershipModal && (
                <UpdateMembershipModal
                    show={showMembershipModal}
                    onDismissModal={setShowMembershipModal}
                    membershipRecord={membershipRecord}
                    setMembershipRecord={setMembershipRecord}
                    membershipValidity={membershipValidity}
                    userDetails={selectedMember}
                    from="#edit-user-modal"
                    allowEditStartDate={subNavState === 'Upcoming'}
                    vIndex={membershipValidity.length + 2}
                    refetchMembership={() => setSubRefresh((r) => !r)}
                />
            )}
        </>
    );
};

export default MembershipDashboard;
